// src/components/CatylexSearch/CatylexSentenceList.tsx

import * as React from "react";
import { useCallback } from "react";
import { CatylexSentence } from "../../interfaces/catylexSearchTypes";
import { ExternalLinkIcon } from "./CatylexIcons";
import CatylexDateBadge from "./CatylexDateBadge";
import { catylexColors } from "./catylexSearchStyles";
import {
  emptyStateStyle,
  containerStyle,
  cardStyle,
  headerRowStyle,
  titleColStyle,
  titleRowStyle,
  titleStyle,
  fileNameStyle,
  reportLinkStyle,
  summaryStyle,
} from "./catylexCardStyles";

interface CatylexSentenceListProps {
  sentences: CatylexSentence[];
  total: number;
}

const quoteStyle: React.CSSProperties = {
  fontSize: "12px",
  lineHeight: "1.5",
  color: catylexColors.foreground,
  margin: "8px 0 0 0",
  padding: "6px 10px",
  borderLeft: `3px solid ${catylexColors.quoteBorder}`,
  backgroundColor: catylexColors.quoteBg,
  borderRadius: "0 4px 4px 0",
};

const pageBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  fontSize: "10px",
  fontWeight: 500,
  marginTop: "6px",
  padding: "2px 6px",
  borderRadius: "4px",
  backgroundColor: catylexColors.pageBg,
  color: catylexColors.pageColor,
  whiteSpace: "nowrap",
};

const formatPages = (pages: number[]): string =>
  pages.length === 1 ? `Page ${pages[0]}` : `Pages ${pages.join(", ")}`;

const CatylexSentenceList: React.FC<CatylexSentenceListProps> = ({ sentences, total }) => {
  const handleOpenReport = useCallback((url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (/^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  if (!sentences || sentences.length === 0) {
    return <div style={emptyStateStyle}>No clauses found matching your search</div>;
  }

  return (
    <div style={containerStyle}>
      {sentences.map((sentence, idx) => {
        const fileName = sentence.contract_name;
        const displayTitle =
          sentence.display_title && sentence.display_title.trim()
            ? sentence.display_title
            : fileName;
        const showFileName = !!fileName && fileName !== displayTitle;

        return (
          <div key={`${sentence.contract_id}-${idx}`} style={cardStyle}>
            <div style={headerRowStyle}>
              <CatylexDateBadge isoDate={sentence.agreement_date} />
              <div style={titleColStyle}>
                <div style={titleRowStyle}>
                  <p style={titleStyle} title={displayTitle}>
                    {displayTitle}
                  </p>
                  {sentence.all_terms_report_url && (
                    <button
                      style={reportLinkStyle}
                      onClick={(e) => handleOpenReport(sentence.all_terms_report_url!, e)}
                      aria-label={`Open terms report for ${displayTitle}`}
                      title="View terms report in a new window"
                    >
                      <ExternalLinkIcon />
                    </button>
                  )}
                </div>
                {showFileName && (
                  <p style={fileNameStyle} title={fileName}>
                    {fileName}
                  </p>
                )}
              </div>
            </div>

            <p style={quoteStyle}>{sentence.text}</p>
            {sentence.pages && sentence.pages.length > 0 && (
              <span style={pageBadgeStyle}>{formatPages(sentence.pages)}</span>
            )}
          </div>
        );
      })}
      {total > sentences.length && (
        <p style={summaryStyle}>
          Using first {sentences.length} of {total} results
        </p>
      )}
    </div>
  );
};

export default CatylexSentenceList;
